import {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {
  Alert,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  AccumProfile,
  findInvalidProfile,
  InvalidProfile,
  listProfiles,
  newProfile,
  saveProfiles,
  validateProfiles
} from '_logics/LFCPgHistoryAccumUtil';
import VolatilePatternsEditor from './VolatilePatternsEditor';
import LFCButton from '_components/inputs/LFCButton';

// 一覧の見出しと各行で同じ列幅を使う
const ruleRowGrid = {
  display: 'grid',
  gridTemplateColumns: '6rem minmax(0, 1.4fr) minmax(0, 0.8fr) 3rem minmax(0, 1fr)',
  columnGap: 1.5,
  alignItems: 'center'
};
const handlingColor: Record<string, 'primary' | 'secondary' | 'default'> = {
  accumulate: 'primary',
  snapshot: 'secondary',
  git: 'default'
};
// 一覧は「どこに入るか」で並べる。無効なルールは末尾
const handlingOrder: Record<string, number> = {accumulate: 0, snapshot: 1, git: 2};
// 表示用に対象ファイルを最後の "/" でパスとファイル名に分ける（保存値は変えない）
const splitPath = (pattern: string): [string, string] => {
  const index = pattern.lastIndexOf('/');
  return index < 0 ? ['', pattern] : [pattern.slice(0, index), pattern.slice(index + 1)];
};
const ruleSortKey = (profile: AccumProfile) => {
  const [dir, file] = splitPath(profile.path_pattern);
  return [
    profile.enabled ? 0 : 1,
    handlingOrder[profile.handling] ?? 3,
    file,
    dir,
    Number.isFinite(profile.priority) ? profile.priority : Infinity,
    profile.device_pattern
  ] as const;
};
const compareRules = (a: AccumProfile, b: AccumProfile) => {
  const left = ruleSortKey(a);
  const right = ruleSortKey(b);
  for (let i = 0; i < left.length; i++) {
    const x = left[i];
    const y = right[i];
    const diff =
      typeof x === 'string' && typeof y === 'string'
        ? x.localeCompare(y)
        : (x as number) - (y as number);
    if (diff) return diff;
  }
  return 0;
};

export default function ProfileEditor({onClose}: {onClose: () => void}) {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const handlingLabel = (handling: AccumProfile['handling']) =>
    handling === 'git'
      ? t('accum.gitHistory')
      : t(handling === 'snapshot' ? 'snapshot.title' : 'accum.title');
  const ctx = useMemo(() => ({t, snack: enqueueSnackbar}), [t, enqueueSnackbar]);
  const [profiles, setProfiles] = useState<AccumProfile[]>([]);
  const [savedProfiles, setSavedProfiles] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [invalidRule, setInvalidRule] = useState<InvalidProfile | null>(null);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const invalidInputRef = useRef<HTMLInputElement | null>(null);
  const dirty = savedProfiles !== null && JSON.stringify(profiles) !== savedProfiles;
  const selected = profiles.find(profile => profile.id === selectedId);
  // 表示順だけ並べ替える。保存する配列の順序は変えない
  const filteredProfiles = profiles
    .filter(profile =>
      `${profile.device_pattern} ${profile.path_pattern} ${profile.priority} ${profile.handling}`
        .toLowerCase()
        .includes(search.toLowerCase().trim())
    )
    .sort(compareRules);
  const invalidInputRefFor = (field: keyof AccumProfile) =>
    invalidRule?.id === selectedId && invalidRule.field === field ? invalidInputRef : undefined;

  useEffect(() => {
    if (!invalidRule || invalidRule.field === 'id' || selectedId !== invalidRule.id) return;
    const input = invalidInputRef.current;
    if (!input) return;
    input.focus({preventScroll: true});
    input.scrollIntoView?.({block: 'center'});
  }, [invalidRule, selectedId]);

  useEffect(() => {
    let live = true;
    listProfiles(ctx)
      .then(doc => {
        if (live) {
          setProfiles(doc.profiles);
          setSavedProfiles(JSON.stringify(doc.profiles));
          setSelectedId(doc.profiles[0]?.id ?? null);
          setVersion(doc.profile_version);
        }
      })
      .catch(() => {
        if (live) setError('accum.loadFailed');
      })
      .finally(() => {
        if (live) setBusy(false);
      });
    return () => {
      live = false;
    };
  }, [ctx]);

  const update = (id: string, field: keyof AccumProfile, value: unknown) => {
    setProfiles(current =>
      current.map(p => {
        if (p.id !== id) return p;
        if (field === 'handling') {
          return value === 'snapshot'
            ? {
                ...p,
                handling: 'snapshot',
                record_format: null,
                record_delimiter: null,
                key_pattern: null,
                retention_days: null,
                volatile_patterns: null
              }
            : {
                ...p,
                handling: value as 'git' | 'accumulate',
                record_format: p.record_format ?? 'line',
                ...(value === 'git' ? {} : {volatile_patterns: null})
              };
        }
        return {...p, [field]: value};
      })
    );
    if (invalidRule?.id === id && invalidRule.field === field) setInvalidRule(null);
  };
  const add = () => {
    const profile = newProfile();
    setProfiles(current => [...current, profile]);
    setSelectedId(profile.id);
    setSearch('');
    setInvalidRule(null);
  };
  const remove = (id: string) => {
    const index = profiles.findIndex(profile => profile.id === id);
    const next = profiles.filter(profile => profile.id !== id);
    setProfiles(next);
    setSelectedId(next[index]?.id ?? next[index - 1]?.id ?? null);
    setInvalidRule(null);
  };
  const requestClose = () => {
    if (busy) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };
  const save = async () => {
    const invalid = validateProfiles(profiles);
    if (invalid) {
      const first = findInvalidProfile(profiles);
      setInvalidRule(first);
      if (first) {
        setSelectedId(first.id);
        setSearch('');
      }
      setError(invalid);
      return;
    }
    setBusy(true);
    setError('');
    setInvalidRule(null);
    try {
      const doc = await saveProfiles(ctx, profiles);
      setProfiles(doc.profiles);
      setSavedProfiles(JSON.stringify(doc.profiles));
      setSelectedId(current =>
        doc.profiles.some(profile => profile.id === current)
          ? current
          : (doc.profiles[0]?.id ?? null)
      );
      setVersion(doc.profile_version);
      enqueueSnackbar(t('accum.saved'), {variant: 'success'});
    } catch {
      setError('accum.saveFailed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Dialog open fullScreen onClose={requestClose}>
        <DialogTitle sx={{pb: 1}}>{t('accum.profiles')}</DialogTitle>
        <DialogContent sx={{display: 'flex', flexDirection: 'column', minHeight: 0, gap: 1.5}}>
          <Alert severity="info">{t('accum.profileNote')}</Alert>
          <Stack direction="row" spacing={2} alignItems="center">
            {dirty && <Typography color="warning.main">{t('accum.unsavedChanges')}</Typography>}
          </Stack>
          {error && <Alert severity="error">{t(error)}</Alert>}
          {busy && <CircularProgress size={24} />}
          <Box
            sx={{
              display: 'flex',
              flexDirection: {xs: 'column', md: 'row'},
              flex: 1,
              minHeight: 0,
              gap: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                width: {xs: '100%', md: 600},
                minWidth: {md: 440},
                maxHeight: {xs: '40%', md: 'none'},
                minHeight: {xs: 180, md: 0},
                border: 1,
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Box sx={{p: 1.5, borderBottom: 1, borderColor: 'divider'}}>
                <TextField
                  fullWidth
                  size="small"
                  label={t('accum.searchRules')}
                  value={search}
                  disabled={busy}
                  onChange={event => setSearch(event.target.value)}
                />
                {selected && !filteredProfiles.includes(selected) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{display: 'block', mt: 1}}
                  >
                    {t('accum.selectionOutsideFilter')}
                  </Typography>
                )}
              </Box>
              <Box
                aria-hidden
                sx={{
                  ...ruleRowGrid,
                  px: 2,
                  py: 0.5,
                  borderBottom: 1,
                  borderColor: 'divider'
                }}
              >
                {[
                  'accum.handling',
                  'accum.colFile',
                  'accum.colPath',
                  'accum.priority',
                  'accum.colDevice'
                ].map(key => (
                  <Typography
                    key={key}
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={key === 'accum.priority' ? {textAlign: 'right'} : undefined}
                  >
                    {t(key)}
                  </Typography>
                ))}
              </Box>
              <List aria-label={t('accum.ruleList')} sx={{overflowY: 'auto', flex: 1, p: 0}}>
                {filteredProfiles.map(profile => {
                  const [dir, file] = splitPath(profile.path_pattern);
                  return (
                    <ListItem key={profile.id} disablePadding>
                      <ListItemButton
                        component="button"
                        type="button"
                        selected={profile.id === selectedId}
                        disabled={busy}
                        onClick={() => setSelectedId(profile.id)}
                        sx={{
                          ...ruleRowGrid,
                          width: '100%',
                          py: 0.5,
                          textAlign: 'left',
                          borderBottom: 1,
                          borderColor: 'divider',
                          ...(!profile.enabled && {opacity: 0.5}),
                          ...(invalidRule?.id === profile.id && {
                            borderLeft: 3,
                            borderLeftColor: 'error.main'
                          })
                        }}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          color={handlingColor[profile.handling] ?? 'default'}
                          label={
                            profile.enabled ? handlingLabel(profile.handling) : t('accum.disabled')
                          }
                          sx={{height: 20, fontSize: '0.7rem', justifySelf: 'start'}}
                        />
                        <Typography variant="body2" noWrap title={profile.path_pattern}>
                          {file}
                        </Typography>
                        <Typography variant="body2" noWrap title={profile.path_pattern}>
                          {dir === '*' ? t('accum.anyPath') : dir}
                        </Typography>
                        <Typography variant="body2" sx={{textAlign: 'right'}}>
                          {profile.priority}
                        </Typography>
                        <Typography variant="body2" noWrap title={profile.device_pattern}>
                          {profile.device_pattern === '*'
                            ? t('accum.allDevices')
                            : profile.device_pattern}
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {filteredProfiles.length === 0 && (
                  <Typography sx={{p: 2}} color="text.secondary">
                    {t('accum.noRules')}
                  </Typography>
                )}
              </List>
              <Box
                sx={{
                  p: 1.5,
                  borderTop: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'flex-end'
                }}
              >
                <LFCButton color="primary" disabled={busy || version === null} onClick={add}>
                  {t('accum.addProfile')}
                </LFCButton>
              </Box>
            </Box>
            <Box sx={{flex: 1, overflowY: 'auto', minHeight: 0}}>
              {selected ? (
                <Box sx={{border: 1, borderColor: 'divider', p: 2, borderRadius: 1}}>
                  <Stack spacing={2} sx={{pt: 1}}>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                      <TextField
                        fullWidth
                        size="small"
                        label={t('accum.devicePattern')}
                        helperText={t('accum.devicePatternNote')}
                        value={selected.device_pattern}
                        disabled={busy}
                        inputRef={invalidInputRefFor('device_pattern')}
                        error={
                          invalidRule?.id === selected.id && invalidRule.field === 'device_pattern'
                        }
                        onChange={e => update(selected.id, 'device_pattern', e.target.value)}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label={t('accum.pathPattern')}
                        helperText={t('accum.pathPatternNote')}
                        value={selected.path_pattern}
                        disabled={busy}
                        inputRef={invalidInputRefFor('path_pattern')}
                        error={
                          invalidRule?.id === selected.id && invalidRule.field === 'path_pattern'
                        }
                        onChange={e => update(selected.id, 'path_pattern', e.target.value)}
                      />
                      <TextField
                        size="small"
                        label={t('accum.priority')}
                        sx={{minWidth: 120}}
                        type="number"
                        value={selected.priority}
                        disabled={busy}
                        inputRef={invalidInputRefFor('priority')}
                        error={invalidRule?.id === selected.id && invalidRule.field === 'priority'}
                        onChange={e =>
                          update(
                            selected.id,
                            'priority',
                            e.target.value === '' ? NaN : Number(e.target.value)
                          )
                        }
                      />
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                      <TextField
                        select
                        size="small"
                        SelectProps={{native: true}}
                        label={t('accum.handling')}
                        value={selected.handling}
                        disabled={busy}
                        fullWidth
                        onChange={e => update(selected.id, 'handling', e.target.value)}
                      >
                        <option value="git">{t('accum.gitException')}</option>
                        <option value="accumulate">{t('accum.title')}</option>
                        <option value="snapshot">{t('snapshot.title')}</option>
                      </TextField>
                      {selected.handling !== 'snapshot' && (
                        <TextField
                          select
                          size="small"
                          SelectProps={{native: true}}
                          label={t('accum.format')}
                          value={selected.record_format}
                          disabled={busy}
                          fullWidth
                          onChange={e => update(selected.id, 'record_format', e.target.value)}
                        >
                          {['line', 'block', 'keyvalue'].map(format => (
                            <option key={format} value={format}>
                              {t(`accum.format_${format}`)}
                            </option>
                          ))}
                        </TextField>
                      )}
                      {selected.handling !== 'snapshot' && (
                        <TextField
                          size="small"
                          label={t('accum.retention')}
                          sx={{minWidth: 180}}
                          type="number"
                          inputProps={{min: 1, step: 1}}
                          value={selected.retention_days ?? ''}
                          disabled={busy}
                          inputRef={invalidInputRefFor('retention_days')}
                          helperText={t('accum.retentionNote')}
                          error={
                            invalidRule?.id === selected.id &&
                            invalidRule.field === 'retention_days'
                          }
                          onChange={e =>
                            update(
                              selected.id,
                              'retention_days',
                              e.target.value === '' ? null : Number(e.target.value)
                            )
                          }
                        />
                      )}
                    </Stack>
                    {selected.handling === 'snapshot' && (
                      <Alert severity="info">{t('snapshot.retentionRule')}</Alert>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {t('accum.regexHint')}
                    </Typography>
                    {(selected.handling === 'snapshot'
                      ? ['header_pattern']
                      : (['record_delimiter', 'key_pattern', 'header_pattern'] as const)
                    ).map(field => (
                      <TextField
                        key={field}
                        size="small"
                        label={t(`accum.${field}`)}
                        helperText={t(`accum.${field}_help`)}
                        value={selected[field] ?? ''}
                        disabled={busy}
                        inputRef={invalidInputRefFor(field)}
                        fullWidth
                        error={invalidRule?.id === selected.id && invalidRule.field === field}
                        onChange={e => update(selected.id, field, e.target.value || null)}
                      />
                    ))}
                    {selected.handling === 'git' && (
                      <VolatilePatternsEditor
                        items={selected.volatile_patterns ?? []}
                        disabled={busy}
                        error={
                          invalidRule?.id === selected.id &&
                          invalidRule.field === 'volatile_patterns'
                        }
                        onChange={items => update(selected.id, 'volatile_patterns', items)}
                      />
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <FormControlLabel
                        label={t('accum.enabled')}
                        control={
                          <Checkbox
                            checked={selected.enabled}
                            disabled={busy}
                            onChange={e => update(selected.id, 'enabled', e.target.checked)}
                          />
                        }
                      />
                      <LFCButton
                        color="secondary"
                        disabled={busy}
                        onClick={() => remove(selected.id)}
                      >
                        {t('accum.removeRule')}
                      </LFCButton>
                    </Stack>
                  </Stack>
                </Box>
              ) : (
                <Typography color="text.secondary" sx={{p: 2}}>
                  {t('accum.selectRule')}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{borderTop: 1, borderColor: 'divider', px: 2}}>
          <LFCButton disabled={busy} onClick={requestClose}>
            {t('閉じる')}
          </LFCButton>
          <LFCButton color="primary" disabled={busy || version === null} onClick={save}>
            {t('保存')}
          </LFCButton>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmDiscard} onClose={() => setConfirmDiscard(false)}>
        <DialogTitle>{t('accum.discardChanges')}</DialogTitle>
        <DialogActions>
          <LFCButton onClick={() => setConfirmDiscard(false)}>{t('accum.keepEditing')}</LFCButton>
          <LFCButton color="error" onClick={onClose}>
            {t('accum.discard')}
          </LFCButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
